import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ComposedChart, ReferenceLine
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, Users, Activity,
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Download,
  ArrowUpRight, ArrowDownRight, Minus, Clock, Heart,
  FileText, Calendar, Target, Wallet, PieChart as PieIcon,
  Brain, Sparkles, Lightbulb, Zap
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { DataHistoryPanel } from '@/components/DataHistory';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (value === undefined || value === null) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  return `${value.toFixed(1)}%`;
};

// KPI Card Component
const KPICard = ({ title, value, format, benchmark, variance, status, icon: Icon, subtitle }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'critical': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'warning': return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20';
      case 'on_track': return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-800';
    }
  };

  const formatValue = () => {
    if (format === 'currency') return formatCurrency(value);
    if (format === 'percent') return formatPercent(value);
    return value?.toLocaleString() || '0';
  };

  return (
    <Card className="stat-card">
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{formatValue()}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            {benchmark !== null && benchmark !== undefined && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">Benchmark: {format === 'percent' ? formatPercent(benchmark) : benchmark}</span>
                {variance !== undefined && (
                  <Badge className={variance > 0 ? 'bg-red-100 text-red-700' : variance < 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${getStatusColor()}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Cost Category Bar
const CostCategoryBar = ({ category, amount, percent, benchmark, variance, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-emerald-500';
    }
  };

  return (
    <div className="space-y-2 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
      <div className="flex items-center justify-between">
        <span className="font-medium">{category}</span>
        <div className="flex items-center gap-2">
          <span className="font-bold">{formatCurrency(amount)}</span>
          <Badge className={status === 'on_track' ? 'bg-emerald-100 text-emerald-700' : status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
            {formatPercent(percent)}
          </Badge>
        </div>
      </div>
      <div className="relative">
        <Progress value={Math.min(percent, 100)} className="h-2" />
        {/* Benchmark indicator */}
        <div 
          className="absolute top-0 h-2 w-0.5 bg-slate-900 dark:bg-white"
          style={{ left: `${Math.min(benchmark, 100)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Benchmark: {formatPercent(benchmark)}</span>
        <span className={variance > 0 ? 'text-red-600' : 'text-emerald-600'}>
          {variance > 0 ? '+' : ''}{variance.toFixed(1)}% vs benchmark
        </span>
      </div>
    </div>
  );
};

// Burnout Risk Card
const BurnoutRiskCard = ({ clinician }) => {
  const getRiskColor = () => {
    switch (clinician.risk_level) {
      case 'critical': return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'high': return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'medium': return 'border-amber-500 bg-amber-50 dark:bg-amber-900/20';
      default: return 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20';
    }
  };

  return (
    <div className={`p-4 rounded-xl border-l-4 ${getRiskColor()}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold">{clinician.clinician_name}</h4>
          <p className="text-sm text-muted-foreground">Avg {clinician.avg_time_per_patient_minutes} min/patient</p>
        </div>
        <Badge className={clinician.risk_level === 'critical' ? 'bg-red-500' : clinician.risk_level === 'high' ? 'bg-orange-500' : clinician.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}>
          {clinician.burnout_risk_score}% Risk
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="text-center p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg">
          <p className="font-bold">{clinician.total_patients_seen}</p>
          <p className="text-xs text-muted-foreground">Patients</p>
        </div>
        <div className="text-center p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg">
          <p className="font-bold">{clinician.total_hours_worked}h</p>
          <p className="text-xs text-muted-foreground">Hours</p>
        </div>
        <div className="text-center p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg">
          <p className="font-bold">{Math.round((clinician.documentation_hours / clinician.total_hours_worked) * 100)}%</p>
          <p className="text-xs text-muted-foreground">Documentation</p>
        </div>
      </div>
      {clinician.risk_factors?.length > 0 && (
        <div className="mt-3 text-xs text-muted-foreground">
          <p className="font-medium mb-1">Risk Factors:</p>
          <ul className="list-disc list-inside space-y-0.5">
            {clinician.risk_factors.map((factor, i) => (
              <li key={i}>{factor}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function MonthlyFinancialDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [burnoutData, setBurnoutData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMonth, setSelectedMonth] = useState('2026-01');
  
  // AI Insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [quickInsights, setQuickInsights] = useState(null);
  
  // Form state for data entry
  const [formData, setFormData] = useState({
    month: '2026-02',
    revenue: '',
    payroll_wages: '',
    pharmacy: '',
    dme: '',
    supplies: '',
    operating_cost: '',
    facility_cost: '',
    adc: '',
    patients_served: '',
    is_projected: false
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashRes, burnoutRes, quickInsightsRes] = await Promise.all([
        fetch(`${API_URL}/api/monthly-financials/dashboard`, { headers }),
        fetch(`${API_URL}/api/monthly-financials/burnout/time-metrics`, { headers }),
        fetch(`${API_URL}/api/monthly-financials/ai-insights/quick`, { headers })
      ]);

      if (dashRes.ok) setDashboardData(await dashRes.json());
      if (burnoutRes.ok) setBurnoutData(await burnoutRes.json());
      if (quickInsightsRes.ok) setQuickInsights(await quickInsightsRes.json());
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const generateAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/monthly-financials/ai-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiInsights(data);
        toast.success('AI analysis generated successfully');
      } else {
        toast.error('Failed to generate AI analysis');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('Failed to generate AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmitFinancials = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/monthly-financials/monthly`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          revenue: parseFloat(formData.revenue) || 0,
          payroll_wages: parseFloat(formData.payroll_wages) || 0,
          pharmacy: parseFloat(formData.pharmacy) || 0,
          dme: parseFloat(formData.dme) || 0,
          supplies: parseFloat(formData.supplies) || 0,
          operating_cost: parseFloat(formData.operating_cost) || 0,
          facility_cost: parseFloat(formData.facility_cost) || 0,
          adc: parseFloat(formData.adc) || 0,
          patients_served: parseInt(formData.patients_served) || 0
        })
      });

      if (res.ok) {
        toast.success('Financial data saved successfully');
        fetchData();
        setFormData({
          month: '2026-02',
          revenue: '',
          payroll_wages: '',
          pharmacy: '',
          dme: '',
          supplies: '',
          operating_cost: '',
          facility_cost: '',
          adc: '',
          patients_served: '',
          is_projected: false
        });
      }
    } catch (error) {
      toast.error('Failed to save financial data');
    }
  };

  // Pie chart colors
  const COLORS = ['#0f172a', '#0d9488', '#d97706', '#6366f1', '#8b5cf6', '#ec4899'];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="financial-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const kpi = dashboardData?.kpi_summary || {};
  const costBreakdown = dashboardData?.cost_breakdown || [];
  const trendData = dashboardData?.trend_data || [];
  const benchmarks = dashboardData?.benchmarks || {};
  const burnoutMetrics = burnoutData?.metrics || [];
  const burnoutSummary = burnoutData?.summary || {};

  // Cost breakdown for pie chart
  const pieData = costBreakdown.map(c => ({
    name: c.category,
    value: c.amount
  })).filter(c => c.value > 0);

  return (
    <div className="space-y-6" data-testid="monthly-financial-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-primary" />
            Monthly Financial Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Track revenue, costs, margins, and operational KPIs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Revenue"
          value={kpi.revenue}
          format="currency"
          status="info"
          icon={DollarSign}
          subtitle={kpi.current_month}
        />
        <KPICard
          title="Net Margin"
          value={kpi.net_margin_percent}
          format="percent"
          benchmark={kpi.net_margin_benchmark}
          variance={kpi.net_margin_percent - kpi.net_margin_benchmark}
          status={kpi.net_margin_percent < 0 ? 'critical' : kpi.net_margin_percent < 8 ? 'warning' : 'on_track'}
          icon={kpi.net_margin_percent >= 0 ? TrendingUp : TrendingDown}
          subtitle={formatCurrency(kpi.net_profit) + ' profit'}
        />
        <KPICard
          title="Average Daily Census"
          value={kpi.adc}
          format="number"
          status="info"
          icon={Users}
          subtitle={`${kpi.patients_served} patients served`}
        />
        <KPICard
          title="Revenue per Patient Day"
          value={kpi.revenue_per_patient_day}
          format="currency"
          status="info"
          icon={Activity}
          subtitle={`Cost: ${formatCurrency(kpi.cost_per_patient_day)}/day`}
        />
      </div>

      {/* Margin Alert */}
      {kpi.net_margin_percent < benchmarks.net_margin && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 dark:text-red-200">Margin Below Benchmark</p>
            <p className="text-sm text-red-700 dark:text-red-300">
              Current margin of {formatPercent(kpi.net_margin_percent)} is {Math.abs(kpi.net_margin_percent - benchmarks.net_margin).toFixed(1)}% below the {formatPercent(benchmarks.net_margin)} benchmark.
              Primary driver: Payroll at {costBreakdown.find(c => c.category === 'Payroll/Wages')?.percent || 0}% (benchmark: {benchmarks.payroll}%)
            </p>
          </div>
        </div>
      )}

      {/* Quick AI Insights Banner */}
      {quickInsights?.insights?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickInsights.insights.slice(0, 4).map((insight, i) => (
            <div 
              key={i}
              className={`p-3 rounded-lg border-l-4 ${
                insight.type === 'critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                insight.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500' :
                insight.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500' :
                'bg-slate-50 dark:bg-slate-800/50 border-slate-400'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-muted-foreground">{insight.title}</span>
                <span className={`text-sm font-bold ${
                  insight.type === 'critical' ? 'text-red-600' :
                  insight.type === 'warning' ? 'text-amber-600' :
                  insight.type === 'success' ? 'text-emerald-600' :
                  'text-slate-600'
                }`}>{insight.metric}</span>
              </div>
              <p className="text-xs text-muted-foreground">{insight.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="report">Report</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="burnout">Burnout Risk</TabsTrigger>
          <TabsTrigger value="ai-insights" className="flex items-center gap-1">
            <Brain className="h-3 w-3" />
            AI Insights
          </TabsTrigger>
          <TabsTrigger value="entry">Data Entry</TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Enhanced Report Tab */}
        <TabsContent value="report" className="space-y-6">
          <EnhancedReportSection 
            domain="financial"
            title="Financial Health Report"
            description="Revenue, costs, margins, and financial performance metrics with AI insights"
            defaultTimeframe="30d"
          />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue vs Expenses Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Revenue vs Expenses Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'margin' ? `${value}%` : formatCurrency(value),
                          name
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#0f172a" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="expenses" name="Expenses" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="margin" name="Margin %" stroke="#0d9488" strokeWidth={2} dot={{ r: 4 }} />
                      <ReferenceLine yAxisId="right" y={12} stroke="#dc2626" strokeDasharray="3 3" label="12% Target" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Cost Distribution Pie */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieIcon className="h-5 w-5" />
                  Cost Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name.split('/')[0]} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
              <p className="text-sm text-muted-foreground">Total Expenses</p>
              <p className="text-2xl font-bold">{formatCurrency(costBreakdown.reduce((sum, c) => sum + c.amount, 0))}</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
              <p className="text-sm text-muted-foreground">WellSky Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(9560)}</p>
              <p className="text-xs text-muted-foreground">Fixed monthly</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
              <p className="text-sm text-muted-foreground">Patient Related</p>
              <p className="text-2xl font-bold">
                {formatPercent((costBreakdown.filter(c => ['Pharmacy', 'DME', 'Supplies'].includes(c.category)).reduce((sum, c) => sum + c.percent, 0)))}
              </p>
              <p className="text-xs text-muted-foreground">Benchmark: 15%</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
              <p className="text-sm text-muted-foreground">Labor Cost</p>
              <p className="text-2xl font-bold">
                {formatPercent(costBreakdown.find(c => c.category === 'Payroll/Wages')?.percent || 0)}
              </p>
              <p className="text-xs text-muted-foreground">Benchmark: 61%</p>
            </Card>
          </div>
        </TabsContent>

        {/* Cost Analysis Tab */}
        <TabsContent value="costs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cost Category Analysis</CardTitle>
              <CardDescription>Compare actual costs against benchmarks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {costBreakdown.map((cost, i) => (
                <CostCategoryBar key={i} {...cost} />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>ADC & Census Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorAdc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="adc" name="ADC" stroke="#0d9488" fill="url(#colorAdc)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profit/Loss Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <ReferenceLine y={0} stroke="#000" />
                    <Bar 
                      dataKey="profit" 
                      name="Net Profit" 
                      fill="#0d9488"
                      radius={[4, 4, 0, 0]}
                    >
                      {trendData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#0d9488' : '#dc2626'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Burnout Risk Tab */}
        <TabsContent value="burnout" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total Clinicians</p>
              <p className="text-2xl font-bold">{burnoutSummary.total_clinicians || 0}</p>
            </Card>
            <Card className="p-4 bg-red-50 dark:bg-red-900/20">
              <p className="text-sm text-muted-foreground">High Risk</p>
              <p className="text-2xl font-bold text-red-600">{burnoutSummary.high_risk_count || 0}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Avg Time/Patient</p>
              <p className="text-2xl font-bold">{burnoutSummary.avg_time_per_patient || 0} min</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-4 w-4" /> Ideal Range
              </p>
              <p className="text-2xl font-bold">45-60 min</p>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Clinician Burnout Risk Assessment
              </CardTitle>
              <CardDescription>Based on average time spent with patients and workload metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {burnoutMetrics.map((clinician, i) => (
                  <BurnoutRiskCard key={i} clinician={clinician} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Risk Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Risk Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {['critical', 'high', 'medium', 'low'].map((level) => {
                  const count = burnoutSummary.risk_distribution?.[level] || 0;
                  const colors = {
                    critical: 'bg-red-500',
                    high: 'bg-orange-500',
                    medium: 'bg-amber-500',
                    low: 'bg-emerald-500'
                  };
                  return (
                    <div key={level} className="flex-1 text-center p-4 rounded-xl bg-slate-50 dark:bg-slate-800">
                      <div className={`h-3 w-3 rounded-full ${colors[level]} mx-auto mb-2`} />
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground capitalize">{level}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights" className="space-y-4">
          {/* AI Analysis Header */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    Financial AI Analysis
                  </CardTitle>
                  <CardDescription>AI-powered insights and recommendations based on your financial data</CardDescription>
                </div>
                <Button 
                  onClick={generateAiAnalysis} 
                  disabled={aiLoading}
                  className="flex items-center gap-2"
                >
                  {aiLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generate Analysis
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* AI Analysis Summary Cards */}
          {aiInsights?.data_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Revenue
                </p>
                <p className="text-xl font-bold">{formatCurrency(aiInsights.data_summary.revenue)}</p>
              </Card>
              <Card className={`p-4 ${aiInsights.data_summary.margin_vs_benchmark < 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Margin vs Benchmark
                </p>
                <p className={`text-xl font-bold ${aiInsights.data_summary.margin_vs_benchmark < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {aiInsights.data_summary.margin_vs_benchmark >= 0 ? '+' : ''}{aiInsights.data_summary.margin_vs_benchmark?.toFixed(1)}%
                </p>
              </Card>
              <Card className={`p-4 ${aiInsights.data_summary.payroll_percent > 65 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" /> Payroll %
                </p>
                <p className={`text-xl font-bold ${aiInsights.data_summary.payroll_percent > 65 ? 'text-amber-600' : ''}`}>
                  {aiInsights.data_summary.payroll_percent?.toFixed(1)}%
                </p>
              </Card>
              <Card className={`p-4 ${aiInsights.data_summary.high_risk_clinicians > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Heart className="h-3 w-3" /> High Risk Staff
                </p>
                <p className={`text-xl font-bold ${aiInsights.data_summary.high_risk_clinicians > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {aiInsights.data_summary.high_risk_clinicians}
                </p>
              </Card>
            </div>
          )}

          {/* AI Analysis Content */}
          {aiLoading ? (
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <Brain className="h-4 w-4 text-primary animate-pulse" />
                  </div>
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-20 w-full mt-4" />
                <Skeleton className="h-20 w-full" />
              </div>
            </Card>
          ) : aiInsights ? (
            <Card>
              <CardContent className="pt-6">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <div className="space-y-4">
                    {aiInsights.analysis.split('\n').map((line, i) => {
                      // Handle markdown-style headers
                      if (line.startsWith('## ')) {
                        return (
                          <h3 key={i} className="text-lg font-semibold text-primary mt-6 mb-2 flex items-center gap-2">
                            <Lightbulb className="h-4 w-4" />
                            {line.replace('## ', '')}
                          </h3>
                        );
                      }
                      if (line.startsWith('### ')) {
                        return (
                          <h4 key={i} className="text-md font-medium mt-4 mb-1">
                            {line.replace('### ', '')}
                          </h4>
                        );
                      }
                      if (line.startsWith('- ') || line.startsWith('* ')) {
                        return (
                          <div key={i} className="flex items-start gap-2 ml-4">
                            <span className="text-primary mt-1">•</span>
                            <span className="text-sm">{line.replace(/^[-*] /, '')}</span>
                          </div>
                        );
                      }
                      if (line.match(/^\d+\./)) {
                        return (
                          <div key={i} className="flex items-start gap-2 ml-4">
                            <span className="text-primary font-medium">{line.split('.')[0]}.</span>
                            <span className="text-sm">{line.split('.').slice(1).join('.')}</span>
                          </div>
                        );
                      }
                      if (line.trim()) {
                        return <p key={i} className="text-sm text-muted-foreground">{line}</p>;
                      }
                      return null;
                    })}
                  </div>
                </div>
                
                {/* Generated timestamp */}
                <div className="mt-6 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Generated: {new Date(aiInsights.generated_at).toLocaleString()}
                  </span>
                  <span>Analysis for: {aiInsights.data_summary?.month}</span>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <Brain className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Financial AI Analysis</h3>
                  <p className="text-muted-foreground max-w-md">
                    Click "Generate Analysis" to get AI-powered insights about your financial performance, 
                    cost optimization opportunities, and workforce-financial correlations.
                  </p>
                </div>
                <Button onClick={generateAiAnalysis} className="mt-2">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Analysis
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Data Entry Tab */}
        <TabsContent value="entry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Monthly Financial Data Entry
              </CardTitle>
              <CardDescription>Enter monthly financial metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitFinancials} className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="month">Month</Label>
                    <Input
                      id="month"
                      type="month"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="revenue">Revenue ($)</Label>
                    <Input
                      id="revenue"
                      type="number"
                      placeholder="1049172"
                      value={formData.revenue}
                      onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="adc">ADC</Label>
                    <Input
                      id="adc"
                      type="number"
                      placeholder="166"
                      value={formData.adc}
                      onChange={(e) => setFormData({ ...formData, adc: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="patients_served">Patients Served</Label>
                    <Input
                      id="patients_served"
                      type="number"
                      placeholder="233"
                      value={formData.patients_served}
                      onChange={(e) => setFormData({ ...formData, patients_served: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-4">Cost Categories</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="payroll_wages">Payroll/Wages ($)</Label>
                      <Input
                        id="payroll_wages"
                        type="number"
                        placeholder="685652"
                        value={formData.payroll_wages}
                        onChange={(e) => setFormData({ ...formData, payroll_wages: e.target.value })}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 61%</p>
                    </div>
                    <div>
                      <Label htmlFor="pharmacy">Pharmacy ($)</Label>
                      <Input
                        id="pharmacy"
                        type="number"
                        placeholder="39483"
                        value={formData.pharmacy}
                        onChange={(e) => setFormData({ ...formData, pharmacy: e.target.value })}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 5%</p>
                    </div>
                    <div>
                      <Label htmlFor="dme">DME ($)</Label>
                      <Input
                        id="dme"
                        type="number"
                        placeholder="45775"
                        value={formData.dme}
                        onChange={(e) => setFormData({ ...formData, dme: e.target.value })}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 5%</p>
                    </div>
                    <div>
                      <Label htmlFor="supplies">Supplies ($)</Label>
                      <Input
                        id="supplies"
                        type="number"
                        placeholder="26340"
                        value={formData.supplies}
                        onChange={(e) => setFormData({ ...formData, supplies: e.target.value })}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 5%</p>
                    </div>
                    <div>
                      <Label htmlFor="operating_cost">Operating Cost ($)</Label>
                      <Input
                        id="operating_cost"
                        type="number"
                        placeholder="178252"
                        value={formData.operating_cost}
                        onChange={(e) => setFormData({ ...formData, operating_cost: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 7%</p>
                    </div>
                    <div>
                      <Label htmlFor="facility_cost">Facility Cost ($)</Label>
                      <Input
                        id="facility_cost"
                        type="number"
                        placeholder="52000"
                        value={formData.facility_cost}
                        onChange={(e) => setFormData({ ...formData, facility_cost: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Benchmark: 4%</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_projected"
                      checked={formData.is_projected}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_projected: checked })}
                    />
                    <Label htmlFor="is_projected">Projected (not actual)</Label>
                  </div>
                  <Button type="submit">
                    Save Financial Data
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-6">
          <SectionHeader 
            icon={Clock} 
            title="Financial Data History" 
            subtitle="View timeline of financial data entries and audit logs"
          />
          <DataHistoryPanel 
            category="financial" 
            title="Monthly Financial Data" 
            defaultExpanded={true}
            isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
